import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';

function getDoctorLabel(doc) {
  const name = `${doc.firstName} ${doc.lastName}`;
  const addr = doc.addresses && doc.addresses.length > 0 ? doc.addresses[0] : null;
  if (addr) {
    const parts = [addr.street, addr.city].filter(Boolean);
    return parts.length > 0 ? `${name} — ${parts.join(', ')}` : name;
  }
  return name;
}

export default function PractitionerMultiSelect({ doctors, selectedIds, onChange }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = doctors.filter((doc) => {
    const label = getDoctorLabel(doc).toLowerCase();
    return label.includes(search.toLowerCase());
  });

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((d) => d !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedDocs = doctors.filter((d) => selectedIds.includes(d._id));

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(!open)}
        className="w-full min-h-[50px] px-4 py-2 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] cursor-pointer bg-white focus-within:ring-2 focus-within:ring-[#0089ff]/20 focus-within:border-[#0089ff] flex items-center flex-wrap gap-1.5"
      >
        {selectedDocs.length === 0 && (
          <span className="text-[#94a3b8]">{t('Select Providers')}</span>
        )}
        {selectedDocs.map((doc) => (
          <span
            key={doc._id}
            className="inline-flex items-center gap-1 bg-[#e6f1ff] text-[#0089ff] text-xs font-medium px-2 py-1 rounded-md"
          >
            {doc.firstName} {doc.lastName}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(doc._id);
              }}
              className="text-[#0089ff] hover:text-red-500 text-sm leading-none"
            >
              &times;
            </button>
          </span>
        ))}
        <span className="material-symbols-outlined ml-auto text-[#64748b] text-[20px] flex-shrink-0">
          expand_more
        </span>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#d6dce8] rounded-[8px] shadow-lg max-h-[240px] overflow-hidden">
          <div className="p-2 border-b border-[#e2e8f0]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search providers...')}
              className="w-full px-3 py-2 border border-[#e2e8f0] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0089ff]"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-[180px]">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-[#94a3b8]">{t('No providers found')}</div>
            ) : (
              filtered.map((doc) => {
                const isSelected = selectedIds.includes(doc._id);
                return (
                  <div
                    key={doc._id}
                    onClick={() => toggle(doc._id)}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#f8fafc] ${isSelected ? 'bg-[#f0f7ff]' : ''}`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#0089ff] border-[#0089ff]' : 'border-[#d6dce8]'}`}
                    >
                      {isSelected && (
                        <span className="text-white text-[10px] font-bold">&#10003;</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-[#24315d] font-medium truncate">
                        {doc.firstName} {doc.lastName}
                      </p>
                      {doc.addresses && doc.addresses.length > 0 && (
                        <p className="text-xs text-[#64748b] truncate">
                          {[doc.addresses[0].street, doc.addresses[0].city, doc.addresses[0].state]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
